import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Search, MessageCircle } from "lucide-react";

export const Route = createFileRoute("/admin_/query-log")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "What people are asking — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminQueryLogPage,
});

type Row = {
  id: string;
  source: "support" | "search";
  query: string;
  route: string | null;
  created_at: string;
};

type SourceFilter = "all" | "support" | "search";
type DaysFilter = 7 | 30 | 90 | 0;

function AdminQueryLogPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<SourceFilter>("all");
  const [days, setDays] = useState<DaysFilter>(30);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      let q = supabase
        .from("query_logs")
        .select("id, source, query, route, created_at")
        .order("created_at", { ascending: false })
        .limit(1000);
      if (source !== "all") q = q.eq("source", source);
      if (days) q = q.gte("created_at", new Date(Date.now() - days * 86400000).toISOString());
      const { data, error } = await q;
      if (cancelled) return;
      if (error) toast.error("Couldn't load the log");
      setRows((data as Row[]) ?? []);
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [source, days]);

  const topics = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const r of rows) {
      const key = r.query.toLowerCase().replace(/\s+/g, " ").trim();
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { label: r.query, count: 1 });
    }
    return [...counts.values()].sort((a, b) => b.count - a.count).slice(0, 40);
  }, [rows]);

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>

        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">What people are asking</h1>
          <p className="text-sm text-muted-foreground">
            Questions typed into the support bubble and phrases typed into the search box.
          </p>
        </header>

        <div className="flex flex-wrap gap-3">
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as SourceFilter)}
            className="rounded-md border border-border bg-card px-3 py-2 text-sm"
            aria-label="Filter by where it was typed"
          >
            <option value="all">Support bubble and search</option>
            <option value="support">Support bubble only</option>
            <option value="search">Search box only</option>
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value) as DaysFilter)}
            className="rounded-md border border-border bg-card px-3 py-2 text-sm"
            aria-label="Filter by time period"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={0}>All time</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
            Nothing recorded yet for this period.
          </p>
        ) : (
          <>
            <section className="rounded-lg border border-border bg-card p-5">
              <h2 className="mb-3 text-lg font-medium">Most common topics</h2>
              <ol className="space-y-2">
                {topics.map((t) => (
                  <li key={t.label} className="flex items-baseline justify-between gap-4 text-sm">
                    <span className="truncate">{t.label}</span>
                    <span className="shrink-0 text-muted-foreground">{t.count}×</span>
                  </li>
                ))}
              </ol>
            </section>

            <section className="rounded-lg border border-border bg-card p-5">
              <h2 className="mb-3 text-lg font-medium">Most recent ({rows.length})</h2>
              <ul className="space-y-3">
                {rows.slice(0, 200).map((r) => (
                  <li key={r.id} className="flex items-start gap-3 text-sm">
                    {r.source === "support" ? (
                      <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <Search className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0">
                      <p className="break-words">{r.query}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("en-GB")}
                        {r.route ? ` · ${r.route}` : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
